// UK Phone Number Formatting Utilities

/**
 * Formats a phone number to UK format
 * Accepts various formats and normalizes to UK standard
 * @param value - Raw phone number input
 * @returns Formatted phone number
 */
export const formatUKPhoneNumber = (value: string): string => {
  // Remove all non-digit characters except + at the start
  let cleaned = value.replace(/[^\d+]/g, '');

  // Preserve + only at the start
  if (cleaned.includes('+') && !cleaned.startsWith('+')) {
    cleaned = cleaned.replace(/\+/g, '');
  }

  // Get just the digits for formatting
  const digits = cleaned.replace(/\D/g, '');

  // Don't format if too short
  if (digits.length < 3) {
    return value;
  }

  // Handle UK phone numbers
  if (cleaned.startsWith('+44')) {
    // International format starting with +44
    const ukDigits = digits.slice(2);
    return formatUKDigits(ukDigits, '+44');
  } else if (digits.startsWith('44')) {
    // International format starting with 44
    const ukDigits = digits.slice(2);
    return formatUKDigits(ukDigits, '+44');
  } else if (digits.startsWith('0')) {
    // UK format starting with 0
    return formatUKDigits(digits, '');
  } else if (digits.length <= 11) {
    // For partial numbers, just return them grouped nicely
    return digits;
  }

  // Return cleaned value if doesn't match UK patterns
  return cleaned;
};

/**
 * Format UK digits into proper display format
 */
const formatUKDigits = (digits: string, prefix: string): string => {
  // Remove leading 0 if we have international prefix
  if (prefix && digits.startsWith('0')) {
    digits = digits.slice(1);
  }

  const totalLength = prefix ? digits.length : digits.length;
  const targetLength = prefix ? 10 : 11;

  // Don't format incomplete numbers
  if (totalLength < 6) {
    return prefix ? `${prefix} ${digits}` : digits;
  }

  // Format based on UK number types
  if (digits.startsWith('07') || (prefix && digits.startsWith('7'))) {
    // Mobile numbers: 07xxx xxxxxx or +44 7xxx xxxxxx
    if (prefix) {
      const parts = [
        prefix,
        digits.slice(0, 4),
        digits.slice(4, Math.min(10, digits.length))
      ].filter(p => p);
      return parts.join(' ');
    } else {
      const parts = [
        digits.slice(0, 5),
        digits.slice(5, Math.min(11, digits.length))
      ].filter(p => p);
      return parts.join(' ');
    }
  } else if (digits.startsWith('02') || (prefix && digits.startsWith('2'))) {
    // London and other 02x numbers: 020 xxxx xxxx or +44 20 xxxx xxxx
    if (prefix) {
      const parts = [
        prefix,
        digits.slice(0, 2),
        digits.slice(2, 6),
        digits.slice(6, Math.min(10, digits.length))
      ].filter(p => p);
      return parts.join(' ');
    } else {
      const parts = [
        digits.slice(0, 3),
        digits.slice(3, 7),
        digits.slice(7, Math.min(11, digits.length))
      ].filter(p => p);
      return parts.join(' ');
    }
  } else {
    // Other geographic numbers: 01xxx xxxxxx
    if (prefix) {
      const parts = [
        prefix,
        digits.slice(0, 4),
        digits.slice(4, Math.min(10, digits.length))
      ].filter(p => p);
      return parts.join(' ');
    } else {
      const parts = [
        digits.slice(0, 5),
        digits.slice(5, Math.min(11, digits.length))
      ].filter(p => p);
      return parts.join(' ');
    }
  }
};

/**
 * Validates if a phone number is a valid UK number
 */
export const isValidUKPhoneNumber = (phone: string): boolean => {
  const digits = phone.replace(/\D/g, '');

  // UK mobile numbers (07xxx xxxxxx)
  if (digits.match(/^(44)?0?7\d{9}$/)) return true;

  // UK landline numbers (01xxx xxxxxx, 02x xxxx xxxx)
  if (digits.match(/^(44)?0?[12]\d{9}$/)) return true;

  // UK non-geographic numbers (03xxx xxxxxx)
  if (digits.match(/^(44)?0?3\d{9}$/)) return true;

  return false;
};

/**
 * Handle phone input change with formatting
 */
export const handlePhoneInputChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  onChange: (value: string) => void
): void => {
  const input = e.target.value;

  // Allow deletion
  if (input.length < e.target.value.length) {
    onChange(input);
    return;
  }

  // Only allow digits, spaces, +, and hyphens
  const cleaned = input.replace(/[^\d\s+-]/g, '');

  // Don't format if user is in the middle of typing international code
  if (cleaned.startsWith('+') && cleaned.length < 4) {
    onChange(cleaned);
    return;
  }

  // Apply formatting
  const formatted = formatUKPhoneNumber(cleaned);
  onChange(formatted);
};